import { CognitoHostedUIIdentityProvider, Auth } from '@aws-amplify/auth'
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js'
import { AuthOptions } from '@aws-amplify/auth/lib-esm/types'
import { Project } from './project'
import { Res } from './types/responses'
import { getApi } from './api'

async function oauth(provider: CognitoHostedUIIdentityProvider) {
  await Auth.federatedSignIn({
    provider,
  })
}
const Cognito = {
  appleOauth: async function () {
    return oauth(CognitoHostedUIIdentityProvider.Apple)
  },
  changePassword: async function (
    username: string,
    code: string,
    password: string,
  ) {
    return await Auth.forgotPasswordSubmit(username, code, password)
  },
  completeNewPassword: async function (newPassword: string) {
    return await Auth.completeNewPassword(this.user, newPassword)
  },
  confirmSignIn: async function (code: string) {
    return await Auth.confirmSignIn(this.user, code, 'SOFTWARE_TOKEN_MFA')
  },
  confirmSignUp: async function (username: string, code: string) {
    return await Auth.confirmSignUp(username, code)
  },

  currentAuthenticatedUser: async function (): Promise<CognitoUser> {
    return await Auth.currentAuthenticatedUser()
  },
  facebookOauth: async function () {
    return oauth(CognitoHostedUIIdentityProvider.Facebook)
  },
  getSession: async function () {
    await Auth.currentAuthenticatedUser()
    return await Auth.currentSession()
  },
  getUser: async function () {
    return await Auth.currentUserInfo().then((res) => {
      return {
        ...res,
        username: res?.attributes?.preferred_username || res.username,
      }
    })
  },
  googleOauth: async function () {
    return oauth(CognitoHostedUIIdentityProvider.Google)
  },
  init: (config: AuthOptions) => {
    Auth.configure({
      Auth: config,
    })
  },

  login: async function (
    username: string,
    password: string,
  ): Promise<CognitoUserSession> {
    this.user = await Auth.signIn({
      password,
      username,
    })
    return await this.getSession()
  },
  loginWithToken: async function (
    username: string,
    data: Res['verifyAccount'],
    // @ts-ignore
  ): Promise<CognitoUserSession> {
    const userData = {
      // @ts-ignore
      Pool: Auth.userPool,
      Username: username,
    }

    const IdToken = new CognitoIdToken({ IdToken: data.id_token })
    const AccessToken = new CognitoAccessToken({
      AccessToken: data.token,
    })
    const RefreshToken = new CognitoRefreshToken({
      RefreshToken: data.refresh_token,
    })
    const userSession = new CognitoUserSession({
      AccessToken,
      IdToken,
      RefreshToken,
    })
    const cognitoUser = new CognitoUser(userData)
    cognitoUser.setSignInUserSession(userSession)

    await new Promise((resolve) => {
      cognitoUser.refreshSession(RefreshToken, () => {
        resolve(null)
      })
    })
    // @ts-ignore
    if (Auth.userPool.storage?.sync) {
      // @ts-ignore
      Auth.userPool.storage.sync()
      // @ts-ignore
      Auth.user = cognitoUser
    }
  },
  logout: async function () {
    if (getApi().getToken?.()) {
      return
    }
    return Auth.signOut()
  },
  resendConfimationCode: async function (email: string) {
    return await Auth.forgotPassword(email)
  },
  resendConfirmSignUp: async function (username: string) {
    return await Auth.resendSignUp(username)
  },
  resetPassword: async function (email: string) {
    return await Auth.forgotPassword(email)
  },
  setupMfa: async function () {
    const user = await this.currentAuthenticatedUser()

    return await Auth.setupTOTP(user)
  },
  signUp: async function (
    username: string,
    password: string,
    attributes?: Record<any, any>,
  ) {
    try {
      const { user, userConfirmed } = await Auth.signUp({
        attributes,
        password,
        username,
      })
      return {
        user,
        userConfirmed,
      }
    } catch (error) {
      console.log('error signing up:', error)
      throw error
    }
  },
  user: null,
  verifyMfa: async function (authCode: string) {
    const user = await this.currentAuthenticatedUser()
    const res = await Auth.verifyTotpToken(user, authCode)
    await Auth.setPreferredMFA(user, 'TOTP')
    return res
  },
}

export default Cognito
